import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
// import { ToasterService } from 'angular-toaster';

import swal, { SweetAlertResult } from 'sweetalert2';
import { RequestMetaService } from '../../core/services/request-meta.service';

export enum S1PopUpType {
  Success = "success",
  Warning = "warning",
  Error = "error",
  Info = "info",
  Question = "question"
}

@Injectable({
  providedIn: 'root'
})
export class S1UIService {

  private timerPopup = 5000;

  constructor(private translate: TranslateService, private requestMeta: RequestMetaService) { }

  // Spinner
  showSpinner() {

    swal.fire({
      title: this.translate.instant('s1.swal.loading'),
      didOpen: function () {
          swal.showLoading();
      }
    });

  }

  closeSpinner() {
    swal.close();
  }

  // Popup
  showPopup(type: S1PopUpType, title?: string, text?: string, timer: number = this.timerPopup) {

    const titleTranslated = title ? this.translate.instant(title) : '';
    const textTranslated = text ? this.translate.instant(text) : '';
    // Ogni popup di errore mostra in fondo il codice richiesta (x-request-id/s1-span-id) dell'ultima risposta HTTP
    const footer = type === S1PopUpType.Error ? this.getRequestCodeFooter() : undefined;

    return swal.fire({ title: titleTranslated, text: textTranslated, icon: type, timer: timer, footer: footer });

  }

  // Footer text with the request-id/span-id of the last HTTP response, for error modals (debug/support reference)
  getRequestCodeFooter(): string {
    const { requestId, spanId } = this.requestMeta.get();
    if (!requestId && !spanId) {
      return undefined;
    }
    return `${this.translate.instant('s1.swal.requestCode')}: ${requestId ?? ''} - ${spanId ?? ''}`;
  }

  showPopupWithResponseHeader(type: S1PopUpType, title?: string, text?: string, timer: number = this.timerPopup) {
    const titleTranslated = title ? this.translate.instant(title) : '';
    const textTranslated = text ? this.translate.instant(text) : '';

    return swal.fire({ title: titleTranslated, text: textTranslated, icon: type, timer: timer, footer: this.getRequestCodeFooter() });
  }


  showSuccessPopup(text?: string, timer?: number) {
    return this.showPopup(S1PopUpType.Success, 's1.swal.success', text, timer);
  }
  
  showErrorPopup(text?: string, timer?: number) {
    return this.showPopup(S1PopUpType.Error, 's1.swal.error', text, timer);
  }
  showWarningPopup(text?: string, timer?: number) {
    return this.showPopup(S1PopUpType.Warning, 's1.swal.attention', text, timer);
  }

  showPopupNoPermission(timer?: number) {
    return this.showPopup(S1PopUpType.Warning, 's1.swal.attention',  's1.swal.noPermission', timer);
  }

  showHTTPErrorPopup(error: any) {
    
    let errorTitle = 's1.swal.error';
    let errorMessage = 'genericError';

    if (error.statusText) {
      errorMessage = this.translate.instant(error.statusText);
    }

    if (error.status) {

      errorTitle = "" + (error.status == 499 ? '' : error.status);

      switch (error.status) {
        case 401: // Unauthorized
        case 403: // Forbidden
        case 413: // Content too large
          errorMessage = this.translate.instant('APIErrors.' + error.status);
          break;
      }

    }

    this.showPopupWithResponseHeader(S1PopUpType.Error, errorTitle, errorMessage, null);

  }

  showDialogPopup(title: string, showCancel: boolean = true, yesNoButtons: boolean = false): Promise<SweetAlertResult> {
    return swal.fire({
      title: this.translate.instant('s1.swal.attention'),
      text: this.translate.instant(title),
      showCloseButton: true,
      showCancelButton: showCancel,
      confirmButtonText: this.translate.instant('s1.swal.' + (yesNoButtons ? 'yes' : 'ok')),
      cancelButtonText: this.translate.instant('s1.swal.' + (yesNoButtons ? 'no' : 'cancel')),
    });
  }

  showInputPopup(title: string, text: string, fieldText: string, initialValue: string = null, 
    input: ('text' | 'email' | 'password' | 'number' | 'tel' | 'range' | 'textarea' 
    | 'select' | 'radio' | 'checkbox' | 'file' | 'url') = 'number'): Promise<any> {

    return swal.fire({
    title: this.translate.instant(title),
    text: text ? this.translate.instant(text) : '',
    input: input,
    inputValue: initialValue ? initialValue : '',
    inputPlaceholder: this.translate.instant(fieldText)
    });

  }

}
